import { ReactComponent as CheckDefIcon } from "assets/images/icons/checkbox_default.svg";
import { ReactComponent as CheckClearIcon } from "assets/images/icons/checkbox_clear.svg";
import { ReactComponent as CheckFillIcon } from "assets/images/icons/checkbox_fiilled.svg";
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";

import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { v4 as uuidv4 } from "uuid";

import useDataSourceManager from "hooks/useDataSourceManager";
import SearchInput from "components/SearchInput";
import PaginationField from "components/Pagination";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import localStorageUtil from "utils/localStorageUtil";

const FromClientView = ({
  auth,
  onClose,
  onApply,
}) => {
  const theme = useTheme();
  const tableName = "fromClientIpAddressTable";
  const { dataMap, getData, sendRequest } = useDataSourceManager();

  const [clientsUuid, _setClientsUuid] = useState(uuidv4());
  const [searchVal, setSearchVal] = useState("");
  const [originalIpAddressItemList, setOriginalIpAddressItemList] = useState([]);
  const [ipAddressItemList, setIpAddressItemList] = useState([]);
  const [selectedIndexes, setSelectedIndexes] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(localStorageUtil.getItemField("resultsPerPageObject", tableName) || 10);

  const fetchClientDeviceList = () => {
    const standalone = !auth.orgs || Object.keys(auth.orgs).length === 0;
    const apiKey = standalone
      ? "/orgs/:orgId/sites/:siteId/clients.read"
      : "/ents/:entId/orgs/:orgId/sites/:siteId/clients.read";
    const restfulParams = standalone
      ? {
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      }
      : {
        entId: auth.realm?.id,
        orgId: auth.orgs?.id,
        siteId: auth.sites?.id,
      };

    sendRequest(apiKey, clientsUuid, restfulParams);
  };

  const filterIpAddressList = (searchValue) => {
    setSearchVal(searchValue);
    setSelectedIndexes([]);
    setCurrentPage(0);

    if (!searchValue || searchValue.trim() === "") {
      setIpAddressItemList(originalIpAddressItemList);
      return;
    }

    const lowerSearchValue = searchValue.toLowerCase();
    const filteredList = originalIpAddressItemList.filter(item =>
      item.ipAddress.toLowerCase().includes(lowerSearchValue) ||
      (item.description && item.description.toLowerCase().includes(lowerSearchValue))
    );

    setIpAddressItemList(filteredList);
  };

  const handleApply = () => {
    const selectedIpAddressItems = selectedIndexes
      .map(index => ipAddressItemList[index])
      .filter(Boolean);
    onApply(selectedIpAddressItems);
    onClose();
  };

  useEffect(() => {
    fetchClientDeviceList();
  }, []);

  useEffect(() => {
    const clientsResponse = getData(clientsUuid);
    if (clientsResponse && clientsResponse.data) {
      const updatedIpAddressList = clientsResponse.data
        .map(client => ({
          id: client.clientId || client.id || client.ipAddress || client.ipv4?.ipAddress,
          ipAddress: client.ipAddress || client.ipv4?.ipAddress,
          subnet: "32",
          description: client.clientName || client.name || "",
        }))
        .filter(client => client.ipAddress)
        .map(client => ({
          id: client.id,
          ipAddress: client.ipAddress,
          subnet: "32",
          description: client.description,
        }));

      setOriginalIpAddressItemList(updatedIpAddressList);
      setIpAddressItemList(updatedIpAddressList);
    }
  }, [dataMap?.[clientsUuid]]);

  useEffect(() => {
    setCurrentPage(0);
  }, [rowsPerPage]);

  const pagedItems = ipAddressItemList.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage);
  const startRecord = ipAddressItemList.length === 0 ? 0 : currentPage * rowsPerPage + 1;
  const endRecord = Math.min((currentPage + 1) * rowsPerPage, ipAddressItemList.length);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[3],
            paddingLeft: "20px",
          }}
        >
          Add from Client List
        </Typography>
        <Button
          onClick={onClose}
          sx={{
            backgroundColor: theme.palette.primary.transparent,
            boxShadow: "none",
            "&:hover": { backgroundColor: theme.palette.primary.transparent },
          }}
        >
          <CloseIcon />
        </Button>
      </Box>

      <Box sx={{ margin: "20px 10px 10px", paddingLeft: "10px" }}>
        <SearchInput value={searchVal} onChange={filterIpAddressList} />
      </Box>

      <Box sx={{ margin: "0 10px 0 20px" }}>
        <TableContainer
          component={Paper}
          sx={{
            "&.MuiPaper-root": {
              boxShadow: "none",
              border: "none",
            },
          }}
          style={{ height: "300px", overflowY: "auto" }}
        >
          <Table
            sx={{
              borderCollapse: "collapse",
              "& td, & th": {
                border: "none",
              },
              "& thead tr": {
                borderBottom: `1px solid ${theme.palette.primary.borderGreyPrimary}`,
              },
              "& tbody tr:nth-of-type(odd)": {
                backgroundColor: theme.palette.primary.white,
              },
              "& tbody tr:nth-of-type(even)": {
                backgroundColor: "rgb(246, 246, 246)",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "48px", padding: "8px 16px" }}>
                  <Checkbox
                    icon={<CheckDefIcon style={{ width: "25px", height: "25px" }} />}
                    checkedIcon={<CheckFillIcon style={{ width: "25px", height: "25px" }} />}
                    indeterminateIcon={<CheckClearIcon style={{ width: "25px", height: "25px" }} />}
                    indeterminate={
                      selectedIndexes.length > 0 &&
                      selectedIndexes.length < ipAddressItemList.length
                    }
                    checked={
                      ipAddressItemList.length > 0 &&
                      selectedIndexes.length === ipAddressItemList.length
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIndexes(ipAddressItemList.map((_, index) => index));
                      } else {
                        setSelectedIndexes([]);
                      }
                    }}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  IP Address
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  Description (Optional)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ipAddressItemList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} sx={{ textAlign: "center", padding: "20px" }}>
                    No Entry in the table
                  </TableCell>
                </TableRow>
              ) : (
                pagedItems.map((item, index) => {
                  const originalIndex = currentPage * rowsPerPage + index;
                  return (
                    <TableRow key={`${item.id || item.ipAddress}-${originalIndex}`}>
                      <TableCell sx={{ width: "48px", padding: "8px 16px" }}>
                        <Checkbox
                          icon={<CheckDefIcon style={{ width: "25px", height: "25px" }} />}
                          checkedIcon={<CheckFillIcon style={{ width: "25px", height: "25px" }} />}
                          checked={selectedIndexes.includes(originalIndex)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedIndexes([...selectedIndexes, originalIndex]);
                            } else {
                              setSelectedIndexes(selectedIndexes.filter(i => i !== originalIndex));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>{item.ipAddress}</TableCell>
                      <TableCell>{item.description}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography sx={{ marginTop: "8px", color: theme.palette.primary.greyPrimary }}>
          Showing {startRecord} - {endRecord} of {ipAddressItemList.length} records
        </Typography>
        <Box mt={1} mb={2} display="flex" justifyContent="center">
          <PaginationField
            name={tableName}
            pageCount={ipAddressItemList?.length > 0 ? Math.max(1, Math.ceil((ipAddressItemList?.length ?? 0) / rowsPerPage)) : 0}
            pageIndex={currentPage}
            pageSize={rowsPerPage}
            onPageChange={page => setCurrentPage(page)}
            onPageSizeChange={size => {
              setCurrentPage(0);
              setRowsPerPage(size);
            }}
          />
        </Box>
      </Box>

      <Box sx={{ margin: "24px 0 16px auto", display: "flex", justifyContent: "center" }}>
        <OutlineButton onClick={onClose} sx={{ marginRight: "20px" }} width="100px" height="32px">
          Cancel
        </OutlineButton>
        <SolidButton onClick={handleApply} width="130px" height="32px" disabled={selectedIndexes.length === 0}>
          Apply ({selectedIndexes.length})
        </SolidButton>
      </Box>
    </Box>
  );
};

export default React.memo(FromClientView);
